import { apiPostJSON } from "../../client.js";
import { requestsCurrent } from "../../core/request-coordinator.js";
import { captureDecisionDraftInput, clearDecisionDraft, collectDecisionResponseValues } from "../../domain/decision-drafts.js";
import { startThreadComponentLive } from "../live.js";
import { refreshThreadComponent } from "../snapshot.js";
import { activeThreadTurnFromState, lookupDecision } from "../state.js";
import { requireAPIKey, submitThreadPrompt } from "../action-helpers.js";

export function handleHarnessThreadComponentInput(state, target) {
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) {
    return false;
  }
  if (!target.closest("[data-decision-id]")) {
    return false;
  }
  captureDecisionDraftInput(state.decisionDrafts, target);
  return true;
}

export async function handleHarnessThreadComponentSubmit(state, form, options = {}) {
  if (!(form instanceof HTMLFormElement)) {
    return false;
  }
  if (form.id !== "thread-message-form" || !form.closest("[data-thread-component]")) {
    return false;
  }
  await submitThreadPrompt(state, options.apiKey, form, options, {
    sendingStatus: "Sending turn...",
    successMessage: "Turn queued.",
    async afterLoad({ created, threadItem }) {
      if (created && created.id) {
        startThreadComponentLive(state, options.apiKey, threadItem.id, options);
      }
    }
  });
  return true;
}

export async function handleHarnessThreadComponentClick(state, target, options = {}) {
  if (!(target instanceof Element) || !target.closest("[data-thread-component]")) {
    return false;
  }

  const progressToggle = target.closest("[data-action='toggle-progress']");
  if (progressToggle) {
    const turnID = String(progressToggle.getAttribute("data-turn-id") || "").trim();
    if (turnID) {
      if (state.expandedProgressIDs.has(turnID)) {
        state.expandedProgressIDs.delete(turnID);
      } else {
        state.expandedProgressIDs.add(turnID);
      }
      options.render?.();
    }
    return true;
  }

  const threadCancel = target.closest("[data-action='cancel-thread-run']");
  if (threadCancel) {
    await cancelActiveTurn(state, options.apiKey, options);
    return true;
  }

  const replaceThread = target.closest("[data-action='replace-thread']");
  if (replaceThread) {
    await replaceThreadFromCurrent(state, options.apiKey, options);
    return true;
  }

  const decisionAction = target.closest("[data-action='decision-respond']");
  if (decisionAction) {
    const decisionID = String(decisionAction.getAttribute("data-decision-id") || "").trim();
    const action = String(decisionAction.getAttribute("data-decision-action") || "").trim();
    const decisionForm = decisionAction.closest("[data-decision-id]");
    await submitDecisionResponse(state, options.apiKey, decisionID, action, { ...options, decisionForm });
    return true;
  }

  const decisionCancel = target.closest("[data-action='decision-cancel']");
  if (decisionCancel) {
    const decisionID = String(decisionCancel.getAttribute("data-decision-id") || "").trim();
    await submitDecisionCancel(state, options.apiKey, decisionID, options);
    return true;
  }

  return false;
}

async function cancelActiveTurn(state, apiKey, options) {
  requireAPIKey(apiKey);
  const activeTurn = activeThreadTurnFromState(state);
  const currentThread = state.thread;
  if (!activeTurn || !currentThread) {
    return;
  }
  options.setStatus?.("Canceling...", "busy");
  const canceled = await apiPostJSON(apiKey, `/v1/threads/${encodeURIComponent(currentThread.id)}/cancel`, {});
  await options.refreshCollections?.();
  if (!requestsCurrent(options.request)) {
    return;
  }
  const refreshed = await refreshThreadComponent(state, apiKey, currentThread.id, {
    refreshEventTurnIDs: [activeTurn.id],
    request: options.request
  });
  if (!requestsCurrent(options.request) || !refreshed) {
    return;
  }
  startThreadComponentLive(state, apiKey, currentThread.id, options);
  options.setStatus?.(canceled && canceled.state === "canceled" ? "Turn canceled." : "Cancel requested.", "ok");
  options.render?.();
}

async function replaceThreadFromCurrent(state, apiKey, options) {
  requireAPIKey(apiKey);
  const currentThread = state.thread;
  if (!currentThread) {
    return;
  }
  options.setStatus?.("Creating fresh thread from workspace...", "busy");
  const created = await apiPostJSON(apiKey, "/v1/threads", {
    agent_id: currentThread.agent_id,
    workspace_id: currentThread.workspace_id,
    base_ref: currentThread.last_base_ref
  });
  await options.refreshCollections?.();
  if (!requestsCurrent(options.request)) {
    return;
  }
  options.setStatus?.("Fresh thread created.", "ok");
  await options.onThreadReplaced?.(created.id);
}

async function submitDecisionResponse(state, apiKey, decisionID, action, options) {
  requireAPIKey(apiKey);
  const decisionItem = lookupDecision(state, decisionID);
  if (!decisionItem) {
    throw new Error("Decision not found.");
  }
  if (!action) {
    throw new Error("decision action is required");
  }
  const form = options.decisionForm instanceof HTMLFormElement ? options.decisionForm : null;
  const values = collectDecisionResponseValues(state.decisionDrafts, form, decisionItem);
  options.setStatus?.("Submitting decision...", "busy");
  await apiPostJSON(apiKey, `/v1/decisions/${encodeURIComponent(decisionID)}/respond`, {
    action,
    values,
    resolved_by: options.resolvedBy || "builtin-web"
  });
  clearDecisionDraft(state.decisionDrafts, decisionID);
  await options.refreshCollections?.();
  if (!requestsCurrent(options.request)) {
    return;
  }
  if (state.thread?.id) {
    const refreshed = await refreshThreadComponent(state, apiKey, state.thread.id, {
      refreshEventTurnIDs: decisionItem.turn_id ? [decisionItem.turn_id] : [],
      request: options.request
    });
    if (!requestsCurrent(options.request) || !refreshed) {
      return;
    }
    startThreadComponentLive(state, apiKey, state.thread.id, options);
  }
  options.setStatus?.("Decision submitted.", "ok");
  options.render?.();
}

async function submitDecisionCancel(state, apiKey, decisionID, options) {
  requireAPIKey(apiKey);
  const decisionItem = lookupDecision(state, decisionID);
  if (!decisionItem) {
    throw new Error("Decision not found.");
  }
  options.setStatus?.("Canceling turn...", "busy");
  await apiPostJSON(apiKey, `/v1/decisions/${encodeURIComponent(decisionID)}/cancel`, {
    resolved_by: options.resolvedBy || "builtin-web"
  });
  clearDecisionDraft(state.decisionDrafts, decisionID);
  await options.refreshCollections?.();
  if (!requestsCurrent(options.request)) {
    return;
  }
  if (state.thread?.id) {
    const refreshed = await refreshThreadComponent(state, apiKey, state.thread.id, {
      refreshEventTurnIDs: decisionItem.turn_id ? [decisionItem.turn_id] : [],
      request: options.request
    });
    if (!requestsCurrent(options.request) || !refreshed) {
      return;
    }
    startThreadComponentLive(state, apiKey, state.thread.id, options);
  }
  options.setStatus?.("Turn canceled.", "ok");
  options.render?.();
}
